import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Store, Menu, X, Command, FolderOpen, Wrench, Users,
  BarChart3, Settings, LogOut, Shield, Megaphone, ListTodo, Package,
  Send, BookOpen, ChevronDown, Building2, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Sidebar({ isOpen, onToggle }) {
  const location = useLocation();
  const { user, logout, isOwner } = useAuth();
  const [brandName, setBrandName] = useState("Command Center");
  const [logoUrl, setLogoUrl] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [spacesOpen, setSpacesOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setBrandName(res.data.brand_name || "Command Center");
      const companyId = user?.company_id;
      setLogoUrl(res.data.logo_url && companyId ? `${res.data.logo_url}?company_id=${companyId}&t=${Date.now()}` : null);
    } catch (e) { /* ignore */ }
  };

  const fetchPermissions = async () => {
    try {
      const res = await axios.get(`${API}/my-permissions`);
      setPermissions(res.data.permissions || []);
    } catch (e) {
      setPermissions(["dashboard", "financials", "stores", "equipment", "staff", "tasks", "documents", "products", "marketing", "playbooks", "team", "email-digests", "settings"]);
    }
    setPermissionsLoaded(true);
  };

  useEffect(() => {
    if (user) { fetchSettings(); fetchPermissions(); }
    const handler = () => fetchSettings();
    window.addEventListener("settings-updated", handler);
    return () => window.removeEventListener("settings-updated", handler);
  }, [user]);

  // Auto-expand sections based on current route
  useEffect(() => {
    const p = location.pathname;
    if (p.startsWith("/stores")) setSpacesOpen(true);
    if (["/equipment", "/documents", "/products", "/marketing", "/playbooks", "/team", "/email-digests"].some(r => p.startsWith(r))) setAdminOpen(true);
  }, [location.pathname]);

  const hasPerm = (key) => isOwner || permissions.includes(key);

  const NavItem = ({ to, icon: Icon, label, perm, indent }) => {
    if (perm && !hasPerm(perm)) return null;
    const isActive = to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
    return (
      <NavLink
        to={to} onClick={onToggle}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-colors
          ${indent ? "ml-4 px-3" : ""}
          ${isActive ? "bg-stone-900 text-stone-50" : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"}`}
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        {label}
      </NavLink>
    );
  };

  const SectionToggle = ({ label, icon: Icon, open, onToggle: toggle, children, activeCheck }) => {
    const isChildActive = activeCheck ? activeCheck(location.pathname) : false;
    return (
      <div>
        <button
          onClick={toggle}
          data-testid={`nav-section-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium w-full transition-colors
            ${isChildActive && !open ? "bg-stone-100 text-stone-900" : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"}`}
        >
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 text-left">{label}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && <div className="mt-0.5 space-y-0.5">{children}</div>}
      </div>
    );
  };

  return (
    <>
      <Button
        data-testid="sidebar-mobile-toggle"
        variant="ghost" size="icon" onClick={onToggle}
        className="fixed top-4 left-4 z-50 md:hidden bg-white shadow-md rounded-md"
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </Button>

      {isOpen && <div className="fixed inset-0 bg-black/20 z-30 md:hidden" onClick={onToggle} />}

      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 h-full w-56 bg-white border-r border-stone-200/60 z-40 transform transition-transform duration-300 ease-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-5 py-5 border-b border-stone-200/60">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{ backgroundColor: logoUrl ? "transparent" : "#1c1917" }}>
                {logoUrl ? (
                  <img data-testid="sidebar-logo" src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <Command className="w-4 h-4 text-stone-50" />
                )}
              </div>
              <h1 data-testid="sidebar-brand" className="text-sm font-semibold tracking-tight truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                {brandName}
              </h1>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
            <NavItem to="/" icon={LayoutDashboard} label="Dashboard" perm="dashboard" />
            <NavItem to="/financials" icon={BarChart3} label="Financials" perm="financials" />
            <NavItem to="/providers" icon={Building2} label="Providers" perm="financials" />

            {/* Spaces */}
            {hasPerm("stores") && (
              <SectionToggle
                label="Spaces" icon={Building2}
                open={spacesOpen} onToggle={() => setSpacesOpen(!spacesOpen)}
                activeCheck={(p) => p.startsWith("/stores")}
              >
                <NavItem to="/stores" icon={Store} label="Stores" perm="stores" indent />
              </SectionToggle>
            )}

            <NavItem to="/staff" icon={Users} label="Staff & Hiring" perm="staff" />
            <NavItem to="/tasks" icon={ListTodo} label="Tasks" perm="tasks" />

            {/* Admin */}
            {(hasPerm("equipment") || hasPerm("documents") || hasPerm("products") || hasPerm("marketing") || hasPerm("playbooks") || hasPerm("team") || hasPerm("email-digests")) && (
              <SectionToggle
                label="Admin" icon={ShieldCheck}
                open={adminOpen} onToggle={() => setAdminOpen(!adminOpen)}
                activeCheck={(p) => ["/equipment", "/documents", "/products", "/marketing", "/playbooks", "/team", "/email-digests"].some(r => p.startsWith(r))}
              >
                <NavItem to="/equipment" icon={Wrench} label="Equipment" perm="equipment" indent />
                <NavItem to="/documents" icon={FolderOpen} label="Documents" perm="documents" indent />
                <NavItem to="/products" icon={Package} label="Products" perm="products" indent />
                <NavItem to="/marketing" icon={Megaphone} label="Marketing" perm="marketing" indent />
                <NavItem to="/playbooks" icon={BookOpen} label="Playbooks" perm="playbooks" indent />
                <NavItem to="/team" icon={Shield} label="Team" perm="team" indent />
                <NavItem to="/email-digests" icon={Send} label="Email Digests" perm="email-digests" indent />
              </SectionToggle>
            )}
          </nav>

          {/* Footer */}
          <div className="px-3 py-3 border-t border-stone-200/60 space-y-0.5">
            {(isOwner || permissions.includes("settings")) && (
              <NavLink
                to="/settings" data-testid="nav-settings" onClick={onToggle}
                className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${location.pathname === "/settings" ? "bg-stone-900 text-stone-50" : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"}`}
              >
                <Settings className="w-4 h-4" /> Settings
              </NavLink>
            )}
            <button
              data-testid="logout-btn" onClick={logout}
              className="flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium text-stone-500 hover:text-red-600 hover:bg-red-50 w-full transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
              {user?.name && <span className="ml-auto text-[11px] text-stone-400 truncate max-w-[60px]">{user.name}</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
